<template class="text-filter">
  <CardWithTitle
    :request-status="requestStatus"
    :item-id="itemId"
    :options-data="baseCardOptions"
    @call-option-menu="callOptionMenu"
    @reload="getData"
  >
    <template v-for="(modal, index) in cardOptionsModals">
      <DynamicModalComponent
        v-if="modal.props.value"
        :key="index"
        :component-name="modal.componentName"
        :custom-props="modal.props"
        :events="modal.events"
      />
    </template>

    <v-dialog
      v-model="dialog"
      width="70vw"
      height="80vh"
    >
      <v-card>
        <v-card-actions
          class="text-filter__close-button"
        >
          <DsButton
            v-tooltip="closeLabel"
            #top-right
            color="tertiary"
            name="close"
            :icon="true"
            @click="dialog = false"
          />
        </v-card-actions>
        <v-card-text class="text-filter__modal-style">
          <h3>{{ this.$t('components.TextFilter.faq.title') }}</h3>
          <p>{{ this.$t('components.TextFilter.faq.description') }}</p>

          <h3>{{ this.$t('components.TextFilter.faq2.title') }}</h3>
          <p>{{ this.$t('components.TextFilter.faq2.description') }}</p>

          <h3>{{ this.$t('components.TextFilter.faq3.title') }}</h3>
          <p>{{ this.$t('components.TextFilter.faq3.description') }}</p>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-card
      v-for="post in rawResults"
      :key="post._id"
      outlined
    >
      <v-card-text
        class="card-content"
      >
        <v-list-item
          class="text-filter__card-title"
        >
          {{ getId(post.text) }}
          <v-card-actions>
            <CopyToClipboard
              :content="urlWithoutProtocol(post.post_url)"
            />
            <DsButton
              v-tooltip="viewLabel"
              class="text-filter__clean-sla-buttons"
              color="secondary"
              name="view"
              :icon="true"
              @click="openCard({ pid: post.pid })"
            />
          </v-card-actions>
        </v-list-item>
        <v-list-item>
          <div>
            <span
              class="font-weight-bold"
            >
              {{ $t('components.TextFilter.titles.postedAt') }}
            </span>
            {{ formatDate(post.posted_at) }}
          </div>
        </v-list-item>
        <v-list-item>
          <div>
            <span
              class="font-weight-bold"
            >
              {{ $t('components.TextFilter.titles.tags') }}
            </span>
            {{ getTags(post.tags) }}
          </div>
        </v-list-item>
        <v-list-item>
          <div>
            <span
              class="font-weight-bold"
            >
              {{ $t('components.TextFilter.titles.text') }}
            </span>
            {{ generateSampleText(post.text) }}
          </div>
        </v-list-item>
      </v-card-text>
    </v-card>
    <v-dialog
      v-model="postDialog"
      fullscreen
    >
      <v-card>
        <v-toolbar>
          <v-toolbar-title>{{ $t('components.OperatorsLogs.details') }}</v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <StIcon
              class="close-icon"
              icon-name="close"
              @click="closeCard"
            />
          </v-toolbar-items>
        </v-toolbar>
        <v-card-actions>
          <PostThemeAndSentiment :post="postCard" />
        </v-card-actions>
      </v-card>
    </v-dialog>
    <div
      v-if="!hasPost && !inputLoading"
      class="text-filter__no-data"
    >
      <h2
        class="no-post-title pt-16"
      >
        {{ noPostsLabel }}
      </h2>
    </div>

    <template #top-right-slot>
      <template v-if="hasCustomFilter">
        <DsButton
          v-tooltip="filterButton"
          color="tertiary"
          name="filter"
          :icon="true"
          @click="openGenericFilterModal"
        />
      </template>
      <Badge
        v-if="hasFilterApplied && isEditor"
        type="info"
        :tooltip="$t('components.Badge.label.tooltip.info')"
        :value="$t('components.Badge.label.icon.key')"
        :preset="true"
      />
      <DsButton
        v-tooltip="infoLabel"
        color="tertiary"
        name="questions"
        :icon="true"
        @click="openDialog"
      />
    </template>
  </CardWithTitle>
</template>

<script>
import { DsButton } from '@ds/components';
import { mapActions, mapGetters, mapState } from 'vuex';
import StIcon from '@stilingue/st-icon';
import WarRoomService from '@/services/WarRoomService';
import PostThemeAndSentiment from '@/components/Post/PostThemeAndSentiment';
import { STANDARD_OLD_TIME_STAMP } from '@/utils/date';
import CopyToClipboard from '@/components/CopyToClipboard';
import CardWithTitle from '@/components/cards/models/CardWithTitle';
import cardsModel from '@/mixins/cards-model';
import filtersModel from '@/mixins/filters-model';
import DynamicModalComponent from '@/components/modal-model/DynamicModalComponent';
import EventBus from '@/utils/event-bus';
import Badge from '@/components/Badge';

export default {
  name: 'TextSearch',
  components: {
    CardWithTitle,
    DsButton,
    StIcon,
    PostThemeAndSentiment,
    CopyToClipboard,
    DynamicModalComponent,
    Badge,
  },
  mixins: [
    cardsModel,
    filtersModel,
  ],
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      rawResults: [],
      isEmptyResponse: false,
      success: false,
      dialog: false,
      postDialog: false,
      requestStatus: 'success',
      postCard: null,
      hasCustomFilter: true,
      cardModalMethods: {
        openGenericFilterModal: this.openGenericFilterModalModal,
      },
    };
  },
  computed: {
    infoLabel() {
      return this.$t('components.TextFilter.infoLabel');
    },
    closeLabel() {
      return this.$t('components.TextFilter.closeLabel');
    },
    noPostsLabel() {
      return this.$t('components.TextFilter.noPosts');
    },
    viewLabel() {
      return this.$t('components.TextFilter.viewLabel');
    },
    dateTitle() {
      return `${this.$t('components.TextFilter.titles.postedAt')} `;
    },
    viewPost() {
      return this.$t('components.OperatorsLogs.viewPost');
    },
    filterButton() {
      return this.$t('components.TextFilter.label.tooltip.filterButton');
    },
    ...mapGetters('dashboard', [
      'activeDashboardFilters',
    ]),
    ...mapGetters('filter', [
      'formattedDateRange',
      'getEndDate',
    ]),
    ...mapState('filter', [
      'accountId',
      'universeId',
    ]),
    ...mapState('dashboard', [
      'isStaging',
    ]),
    hasPost() {
      return this.rawResults && this.rawResults.length;
    },
    inputLoading() {
      return this.requestStatus === 'loading';
    },
    color() {
      if (this.rawResults.length) {
        return 'primary';
      }
      return 'tertiary';
    },
  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions('cards', [
      'getWarroomService',
    ]),
    openDialog() {
      this.dialog = true;
    },
    getData(options) {
      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }

      this.applyFilter(options);
    },
    async applyFilter(options) {
      this.rawResults = [];
      this.isEmptyResponse = false;
      this.requestStatus = 'loading';
      this.getWarroomService({
        itemId: this.itemId,
        servicePath: 'topPosts',
        serviceParams: {
          limit: 1000,
        },
      })
        .then(response => {
          this.rawResults = response.posts;
          if (options?.silentRefresh) {
            EventBus.$emit('updated', { itemId: this.itemId });
          }
          this.isEmptyResponse = !this.rawResults?.length;
          this.requestStatus = 'success';
        })
        .catch((err) => {
          console.error(err);
          this.requestStatus = 'error';
        });
    },
    urlWithoutProtocol(url) {
      return url.substring(7);
    },
    removeHTMLtags(text) {
      return text.replace(/<[^>]+>/giu, '');
    },
    generateSampleText(text) {
      const newText = this.generateText(text);
      return `${newText.substring(0, 299)}...`;
    },
    generateText(text) {
      const newText = this.removeHTMLtags(text);
      return newText;
    },
    getTags(tags) {
      return tags.join(', ');
    },
    formatDate(date) {
      return date.substring(0, 11);
    },
    getId(text) {
      const id = text.replace(/<\/?u>/giu, '').replace('.pdf', '').split('<br>')[0];
      return this.removeHTMLtags(id);
    },
    async openCard({ pid, sacCallId }) {
      this.$attrs.filter = { pids: [pid] };
      this.$attrs.filterId = null;
      const defaultParams = {
        date_range: `${STANDARD_OLD_TIME_STAMP}:${this.getEndDate}`,
        account_id: this.accountId,
        universe_id: this.universeId,
        is_staging: this.isStaging,
        channel: 'importedData',
        filter: {
          is_sac: false,
        },
      };
      pid ? (defaultParams.filter.pids = { value: [pid] }) : (defaultParams.filter.sac_call_ids = { value: [sacCallId] });
      WarRoomService.topPosts({ params: defaultParams })
        .then(res => {
          this.postDialog = true;
          this.postCard = res.posts[0];
        }).catch(err => console.error(err));
    },
    closeCard() {
      this.postDialog = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.text-filter {
  &__internal-style {
    width: 100%;
    padding: 1rem;
    display: flex;
  }

  &__buttons {
    display: flex;
  }

  &__button {
    box-shadow: none;
    border-radius: unset;
  }

  &__close-button {
    display: flex;
    flex: end;
    justify-content: flex-end;
  }

  &__card-title {
    display: flex;
    align-items: center;
    font-weight: bold;
    gap: 0.5rem;
  }

  &__modal-style {
    * {
      color: var(--ds-color-snow) !important;
    }
  }

  &__clean-sla-buttons {
    box-shadow: none;
    border-radius: unset;
  }
}

</style>

